// Direct Mixpanel integration for the paywall_popup flow ONLY.
//
// Why this exists
// ---------------
// Every other prelander flow redirects the user to the platform after signup,
// and the backend already emits `registration_started` at /auth/pre-lander time
// (see backend auth.service.ts processRegisterExternalPrelander). The
// `paywall_popup` flow is different: after signup the paywall is rendered
// IN PLACE (createAccount.jsx returns <PaywallPopup> instead of redirecting), so
// a user who registers but never purchases never leaves the LP. This module lets
// the LP emit `registration_started` directly so that cohort isn't lost.
//
// "Count once" guarantee
// ----------------------
// We identify the user by their Mongo _id and stamp a DETERMINISTIC $insert_id
// built by `buildInsertId` (utils/insertId.js) — IDENTICAL to the scheme used by:
//   - the backend  : new-hybrid-marketing.service.ts
//   - the platform : frontend/src/lib/mixpanel/mixpanel.ts
// which is why it lives in its own module rather than here.
// Mixpanel drops events sharing (event name, distinct_id, $insert_id) within
// ~5 days, so even after the user purchases and lands on the platform, this
// event is counted EXACTLY ONCE regardless of how many sides send it. No
// platform-side condition is needed — the shared $insert_id does the deduping.

import mixpanel from "mixpanel-browser";
import { getLanderContent } from "./analytics";
import { getLpSource } from "../config/domainConfig";
import {
  buildInsertId,
  REGISTRATION_COMPLETED,
  REGISTRATION_STARTED,
} from "./insertId";

// Provide these via the LP env (CRA REACT_APP_* prefix). Host defaults to the EU
// ingestion endpoint, matching the platform (lib/mixpanel/mixpanel.ts).
const TOKEN = process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN || "";
const HOST =
  process.env.REACT_APP_MIXPANEL_PROJECT_HOST || "https://api-eu.mixpanel.com";

// Mirrors api.js / analytics.js LP_SOURCE so this LP's registrations can be told
// apart from the other landers in Mixpanel. Resolved from the live hostname
// (swipeyconnect vs swipeyquiz) — see src/config/domainConfig.js.
const LP_SOURCE = getLpSource();
const ORDER_CREATED = "order_created";

let initialized = false;
let warnedNoToken = false;

// Init once, lazily on first use — non-paywall_popup visitors never spin up a
// Mixpanel session. Config mirrors the platform's mixpanel.init.
const ensureInit = () => {
  if (initialized) return true;
  if (!TOKEN) {
    // Loud one-time signal: without the token the whole Mixpanel deliverable is
    // a silent no-op. Make a missing env var observable instead of invisible.
    if (!warnedNoToken) {
      warnedNoToken = true;
      console.warn(
        "[mixpanel] REACT_APP_MIXPANEL_PROJECT_TOKEN is not set — LP Mixpanel tracking is disabled.",
      );
    }
    return false;
  }
  mixpanel.init(TOKEN, {
    api_host: HOST,
    track_pageview: false,
    persistence: "localStorage",
  });
  initialized = true;
  return true;
};

// The prelander registration response returns a JWT `accessToken` whose payload
// carries `userId` (the Mongo _id — see backend types/decoded-jwt.type.ts). We
// decode it purely to read that id for the dedupe key; no signature check is
// needed client-side. Returns null if the token is missing/malformed.
export const getUserIdFromAccessToken = (accessToken) => {
  if (!accessToken || typeof accessToken !== "string") return null;
  const parts = accessToken.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const payload = JSON.parse(json);
    return payload?.userId || null;
  } catch {
    return null;
  }
};

// Flatten the GrowthBook flag map into Mixpanel profile properties, mirroring the
// platform's getFormatedUserExperiments (xDate frontend UseMixpanel.tsx): each
// experiment becomes a top-level property, and an object-valued flag carrying a
// `.name` collapses to that name. This is what makes the popup cohort's profile
// look like the control arm's — the platform sets these on identify, but the popup
// non-buyer never reaches the platform, so we replicate it here from the LP.
const formatExperiments = (experiments) => {
  if (!experiments || typeof experiments !== "object") return {};
  const out = {};
  Object.keys(experiments).forEach((key) => {
    const value = experiments[key];
    out[key] =
      value && typeof value === "object" && value.name ? value.name : value;
  });
  return out;
};

const flushProfileUpdates = () => {
  try {
    const batcher = mixpanel.request_batchers?.people;
    // Absent when batching is disabled — people.set already sent immediately then.
    if (batcher && !batcher.stopped) batcher.flush({ unloading: true });
  } catch {
    // Internal SDK surface: never let a flush attempt break tracking.
  }
};

// people.set + immediate beacon flush. The callback runs once the update is safely
// IN the people queue (the enqueue is async), so flushing from it can't race the
// queue write the way a synchronous flush after people.set would.
const setProfileProps = (props) => {
  mixpanel.people.set(props, () => flushProfileUpdates());
};

// Fire a registration funnel event to Mixpanel for the paywall_popup flow.
// `accessToken` is the JWT returned by the prelander registration response
// (password / magic-link paths). `extraProps` are merged into the event.
// `experiments` is the GrowthBook ab_testing map — set on the PROFILE (not the
// event), mirroring the platform. Returns true when the event was sent.
const trackRegistrationEvent = (
  eventName,
  userId,
  extraProps = {},
  experiments,
  email,
  utm = {},
) => {
  if (!ensureInit()) return false;

  // Without the Mongo _id we can't build the dedupe key, so sending would risk
  // double-counting against the backend's copy. The backend still emits its own
  // copy for this user, so skipping here loses nothing.
  if (!userId) {
    console.warn(`[mixpanel] ${eventName} skipped: no userId`);
    return false;
  }

  try {
    // distinct_id = Mongo _id, matching the backend $user_id and the platform's
    // mixpanel.identify(currentUser._id), so all copies land on one profile.
    mixpanel.identify(userId);
    // Profile enrichment — the experiment variant lives on the PROFILE on the
    // regular flow (platform UseMixpanel.tsx:83 people.set). The popup non-buyer
    // never triggers that, so we set the same experiments here so this cohort is
    // segmentable in Mixpanel just like the control arm. `$email` goes on the
    // PROFILE too: profile updates are last-write-wins (NOT deduped like events),
    // so this reliably attaches the email even though the backend's copy of the
    // registration event may be the one that survives $insert_id deduping.
    if (experiments || email || Object.keys(utm).length) {
      setProfileProps({
        ...(experiments ? formatExperiments(experiments) : {}),
        ...(email ? { $email: email } : {}),
        // Last-touch UTM/attribution on the profile so this LP cohort is
        // segmentable by acquisition source (the backend never sets UTMs on the
        // profile, so this is purely additive and not subject to event dedup).
        ...utm,
        landing_page_source: LP_SOURCE,
      });
    }
    const insertId = buildInsertId(userId, eventName);
    mixpanel.track(eventName, {
      // Deterministic dedupe key — see the file header. This is what makes the
      // event count once across LP + backend + platform. Omitted entirely when it
      // can't be built (see buildInsertId), rather than sent invalid.
      ...(insertId ? { $insert_id: insertId } : {}),
      // Same attribution the GTM/dataLayer events carry (see analytics.js
      // trackEvent) so the Mixpanel copy matches the GTM copy: lp_source +
      // lander-content, with paywall-type passed in via extraProps by the caller.
      lp_source: LP_SOURCE,
      "lander-content": getLanderContent(),
      source: "manual",
      flow: "paywall_popup",
      // Also on the event for convenience; the reliable copy is $email on the
      // profile above (this event prop may lose to the backend copy on dedup).
      ...(email ? { email } : {}),
      // UTM/attribution on the event too, matching the GTM/dataLayer copy
      // (createAccount spreads the same getUtmAndEf() there).
      ...utm,
      ...extraProps,
    });
    return true;
  } catch (error) {
    console.error(`[mixpanel] ${eventName} failed`, error);
    return false;
  }
};

// password / magic-link: the Mongo _id is decoded from the prelander JWT accessToken.
// `experiments` is the GrowthBook ab_testing map (set on the profile). `email` is
// the address the user just registered with — set on the profile as `$email`.
export const trackRegistrationStartedMixpanel = (
  accessToken,
  extraProps = {},
  experiments,
  email,
  utm = {},
) =>
  trackRegistrationEvent(
    REGISTRATION_STARTED,
    getUserIdFromAccessToken(accessToken),
    extraProps,
    experiments,
    email,
    utm,
  );

// Google popup return: the LP has the Mongo _id DIRECTLY (from the return URL's
// `uid` param) — there's no JWT there — so this takes the userId, not a token.
// Deduped against the backend's registration_completed via the shared $insert_id.
// (Google returns no email to the LP, so `email` is normally omitted here.)
export const trackRegistrationCompletedMixpanel = (
  userId,
  extraProps = {},
  experiments,
  email,
  utm = {},
) =>
  trackRegistrationEvent(
    REGISTRATION_COMPLETED,
    userId,
    extraProps,
    experiments,
    email,
    utm,
  );

// order_created (checkout opened) — fired from the popup alongside the GTM
// order_created, with the same data. Identified by the Mongo _id so it lands on
// the user's profile (which already holds $email/UTM from registration). Pass the
// event props via `extraProps`. Returns true when sent.
export const trackOrderCreatedMixpanel = (userId, extraProps = {}) => {
  if (!ensureInit()) return false;
  if (!userId) {
    console.warn("[mixpanel] order_created skipped: no userId");
    return false;
  }
  try {
    mixpanel.identify(userId);
    mixpanel.track(ORDER_CREATED, {
      lp_source: LP_SOURCE,
      "lander-content": getLanderContent(),
      flow: "paywall_popup",
      ...extraProps,
    });
    return true;
  } catch (error) {
    console.error("[mixpanel] order_created failed", error);
    return false;
  }
};

export default {
  trackRegistrationStartedMixpanel,
  trackRegistrationCompletedMixpanel,
  trackOrderCreatedMixpanel,
  getUserIdFromAccessToken,
};
